<template>
  <div class="billing-statement-page">
    <h6>Demonstrativo de faturamento</h6>
    <div class="loading loading-lg" v-if="loading" />
    <div v-else>
      <div class="columns">
        <div class="column col-3 col-md-4 col-sm-12 form-group"
             :class="{'has-error': $v.filters.startDate.$error}">
          <label class="form-label">Data início</label>
          <div class="input-group">
            <dx-input-date
              id="appointment-patient-birth-date"
              class="form-input text-center"
              v-model="filters.startDate"
              @blur="$v.filters.startDate.$touch()"
            />
          </div>
          <template v-if="$v.filters.startDate.$error">
            <div class="form-input-hint"
                 v-if="!$v.filters.startDate.required">Campo obrigatório</div>
            <div class="form-input-hint"
                 v-else-if="!$v.filters.startDate.date" >Data inválida</div>
          </template>
        </div>
        <div class="column col-3 col-md-4 col-sm-12 form-group"
             :class="{'has-error': $v.filters.endDate.$error}">
          <label class="form-label">Data término</label>
          <dx-input-date
            id="end-date"
            class="form-input text-center"
            v-model="filters.endDate"
            @blur="$v.filters.endDate.$touch()"
          />
          <template v-if="$v.filters.endDate.$error">
            <div class="form-input-hint"
                 v-if="!$v.filters.endDate.required">Campo obrigatório</div>
            <div class="form-input-hint"
                 v-else-if="!$v.filters.endDate.date" >Data inválida</div>
          </template>
        </div>
        <div class="column col-3 col-md-4 col-sm-12 form-group">
          <label class="form-label">Agrupado por</label>
          <select id="group" class="form-select" v-model="filters.group">
            <option value="insurance">Convênio</option>
            <option value="professional">Profissional</option>
            <option value="expense">Procedimento</option>
          </select>
        </div>
        <div class="column col-3 col-md-4 col-sm-12 form-group">
          <label class="form-label">Ordenado por</label>
          <select id="group" class="form-select" v-model="filters.sort">
            <option value="date">Data</option>
            <option value="patient">Paciente</option>
          </select>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group">
          <label class="form-label">Status da conta</label>
          <select class="form-select" v-model="filters.status">
            <option value="">[Todas as contas]</option>
            <option value="open">Contas em aberto (Agendamento)</option>
            <option value="in_billing">Contas sendo revisadas (Faturando)</option>
            <option value="closed">Contas fechadas (Envio do XML)</option>
            <option value="finished">Contas finalizadas (Repasse)</option>
            <option value="in_appeal">Contas sendo recursadas (Recurso)</option>
            <option value="canceled">Contas canceladas</option>
          </select>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group">
          <label class="form-label">Repasse</label>
          <select class="form-select" v-model="filters.paid">
            <option value="">[Todos]</option>
            <option value="true">Apenas os realizado</option>
            <option value="false">Apenas não realizado</option>
          </select>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group">
          <label class="form-label">Local</label>
          <select class="form-select" v-model="filters.placeId">
            <option value="">[Todos]</option>
            <option
              v-for="place in places"
              :value="place.id"
              :key="place.id"
            >{{ place.name }}</option>
          </select>
        </div>
        <div class="column col-auto">
          <label class="form-switch">
            <input type="checkbox" v-model="filters.return">
            <i class="form-icon"></i>Exibir retornos
          </label>
        </div>
        <div class="column">
          <label class="form-switch">
            <input type="checkbox" v-model="filters.courtesy">
            <i class="form-icon"></i>Exibir cortesias
          </label>
        </div>
      </div>
      <div class="divider" />
      <div class="button-types">
        <div class="type" @click="openInsuranceModal"
             :class="{loading: insuranceModal.loading}">
          <template v-if="!insuranceModal.loading">
            <strong class="text-primary">Convênios</strong>
            <div class="text-secondary">Clique para personalizar</div>
            <div class="text-small">
              <div v-if="isNumber(getInsurancesSelected)">
                <strong>{{ getInsurancesSelected }}</strong> de
                <strong>{{ insuranceModal.items.length }}</strong> selecionado(s)
              </div>
              <strong class="text-success" v-else-if="getInsurancesSelected">
                Todos selecionados
              </strong>
              <strong class="text-error" v-else-if="!getInsurancesSelected">
                Nenhum selecionado
              </strong>
            </div>
          </template>
        </div>
        <div class="type" @click="openExpenseModal"
             :class="{loading: expenseModal.loading}">
          <template v-if="!expenseModal.loading">
            <strong class="text-primary">Procedimentos</strong>
            <div class="text-secondary">Clique para personalizar</div>
            <div class="text-small">
              <div v-if="isNumber(getExpensesSelected)">
                <strong>{{ getExpensesSelected }}</strong> de
                <strong>{{ expenseModal.items.length }}</strong> selecionado(s)
              </div>
              <strong class="text-success" v-else-if="getExpensesSelected">
                Todos selecionados
              </strong>
              <strong class="text-error" v-else-if="!getExpensesSelected">
                Nenhum selecionado
              </strong>
            </div>
          </template>
        </div>
        <div class="type" @click="openRequestModal"
             :class="{loading: requestModal.loading}">
          <template v-if="!requestModal.loading">
            <strong class="text-primary">Profissionais solicitantes</strong>
            <div class="text-secondary">Clique para personalizar</div>
            <div class="text-small">
              <div v-if="isNumber(getRequestSelected)">
                <strong>{{ getRequestSelected }}</strong> de
                <strong>{{ requestModal.items.length }}</strong> selecionado(s)
              </div>
              <strong class="text-success" v-else-if="getRequestSelected">
                Todos selecionados
              </strong>
              <strong class="text-error" v-else-if="!getRequestSelected">
                Nenhum selecionado
              </strong>
            </div>
          </template>
        </div>
        <div class="type" @click="openProfessionalModal"
             :class="{loading: professionalModal.loading}">
          <template v-if="!professionalModal.loading">
            <strong class="text-primary">Profissionais executantes</strong>
            <div class="text-secondary">Clique para personalizar</div>
            <div class="text-small">
              <div v-if="isNumber(getProfessionalsSelected)">
                <strong>{{ getProfessionalsSelected }}</strong> de
                <strong>{{ professionalModal.items.length }}</strong> selecionado(s)
              </div>
              <strong class="text-success" v-else-if="getProfessionalsSelected">
                Todos selecionados
              </strong>
              <strong class="text-error" v-else-if="!getProfessionalsSelected">
                Nenhum selecionado
              </strong>
            </div>
          </template>
        </div>
      </div>
      <div class="footer">
        <button class="btn btn-gray btn-icon btn-icon-left mr-1"
                :class="{loading: printing}"
                :disabled="printing"
                @click="print('sheet')">
          <fa-icon :icon="['fal', 'file-excel']"/>Gerar planilha
        </button>
        <button class="btn btn-primary btn-icon-left"
                :class="{loading: printing}"
                :disabled="printing"
                @click="print('pdf')">
          <fa-icon :icon="['fal', 'print']"></fa-icon>Imprimir
        </button>
      </div>
    </div>
    <dx-modal
      id="insurance-modal"
      title="Convênios"
      v-model="insuranceModal.show"
      @input="closeInsuranceModal">
      <div class="empty" v-if="insuranceModal.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <div class="empty-title h6">Convênios</div>
        <div class="empty-subtitle">Nenhum convênio disponível</div>
      </div>
      <template v-else>
        <div class="input-group">
          <input
            type="text"
            class="form-input"
            v-model.trim="insuranceModal.search"
            placeholder="Pesquisar convênio"
            v-focus
          >
          <button class="btn btn-neutral btn-icon btn-action input-group-btn"
                  v-if="!insuranceModal.search">
            <fa-icon :icon="['fal', 'search']"/>
          </button>
          <button class="btn btn-gray btn-icon btn-action input-group-btn"
                  @click="insuranceModal.search = ''"
                  v-else>
            <fa-icon :icon="['fal', 'times']"/>
          </button>
        </div>
        <table class="table">
          <tr>
            <th style="width: 50px;">
              <label class="form-checkbox">
                <input type="checkbox"
                       @change="selectAllInsurances"
                       :checked="getInsurancesSelected"
                       :indeterminate.prop="isNumber(getInsurancesSelected)">
                <i class="form-icon"></i>
              </label>
            </th>
            <th>Convênio</th>
          </tr>
          <tr v-for="(item, i) in filteredInsurances" :key="i">
            <td>
              <label class="form-checkbox">
                <input type="checkbox" v-model="item.selected">
                <i class="form-icon"></i>
              </label>
            </td>
            <td>{{ item.name }}</td>
          </tr>
        </table>
      </template>
      <template slot="footer">
        <button class="btn" @click="closeInsuranceModal">OK</button>
      </template>
    </dx-modal>
    <dx-modal
      id="expense-modal"
      title="Procedimentos"
      v-model="expenseModal.show"
      @input="closeExpenseModal">
      <div class="empty" v-if="expenseModal.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <div class="empty-title h6">Procedimentos</div>
        <div class="empty-subtitle">Nenhum procedimento disponível</div>
      </div>
      <template v-else>
        <div class="columns">
          <div class="column col-6">
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model.trim="expenseModal.search"
                placeholder="Pesquisar procedimento"
                v-focus
              >
              <button class="btn btn-neutral btn-icon btn-action input-group-btn"
                      v-if="!expenseModal.search">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
              <button class="btn btn-gray btn-icon btn-action input-group-btn"
                      @click="expenseModal.search = ''"
                      v-else>
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </div>
          </div>
          <div class="column col-6">
            <select class="form-select"
                    v-model="expenseModal.modality">
              <option value="">[Todas as modalidades]</option>
              <option v-for="(text, value, i) in modalities"
                      :value="value" :key="i">{{ value }} - {{ text }}</option>
            </select>
          </div>
        </div>
        <table class="table">
          <tr>
            <th style="width: 50px;">
              <label class="form-checkbox">
                <input type="checkbox"
                       @change="selectAllExpenses"
                       :checked="getExpensesSelected"
                       :indeterminate.prop="isNumber(getExpensesSelected)">
                <i class="form-icon"></i>
              </label>
            </th>
            <th>Procedimento</th>
          </tr>
          <tr v-for="(item, i) in filteredExpenses" :key="i">
            <td>
              <label class="form-checkbox">
                <input type="checkbox" v-model="item.selected">
                <i class="form-icon"></i>
              </label>
            </td>
            <td>{{ item.name }}</td>
          </tr>
        </table>
      </template>
      <template slot="footer">
        <button class="btn" @click="closeExpenseModal">OK</button>
      </template>
    </dx-modal>
    <dx-modal
      id="professional-modal"
      title="Profissionais executantes"
      v-model="professionalModal.show"
      @input="closeProfessionalModal">
      <div class="empty" v-if="professionalModal.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <div class="empty-title h6">Profissionais executantes</div>
        <div class="empty-subtitle">Nenhum profissional disponível</div>
      </div>
      <template v-else>
        <div class="columns">
          <div class="column col-6">
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model.trim="professionalModal.search"
                placeholder="Pesquisar profissional"
                v-focus
              >
              <button class="btn btn-neutral btn-icon btn-action input-group-btn"
                      v-if="!professionalModal.search">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
              <button class="btn btn-gray btn-icon btn-action input-group-btn"
                      @click="professionalModal.search = ''"
                      v-else>
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </div>
          </div>
          <div class="column col-6">
            <select class="form-select"
                    v-model="professionalModal.specialtyCode">
              <option value="">[Todas as especialidades]</option>
              <option v-for="(item, i) in professionalModal.specialties"
                      :value="item.code" :key="i">{{ item.name }}</option>
            </select>
          </div>
        </div>
        <table class="table">
          <tr>
            <th style="width: 50px;">
              <label class="form-checkbox">
                <input type="checkbox"
                       @change="selectAllProfessionals"
                       :checked="getProfessionalsSelected"
                       :indeterminate.prop="isNumber(getProfessionalsSelected)">
                <i class="form-icon"></i>
              </label>
            </th>
            <th>Profissional</th>
          </tr>
          <tr v-for="(item, i) in filteredProfessionals" :key="i">
            <td>
              <label class="form-checkbox">
                <input type="checkbox" v-model="item.selected">
                <i class="form-icon"></i>
              </label>
            </td>
            <td>{{ item.name }}</td>
          </tr>
        </table>
      </template>
      <template slot="footer">
        <button class="btn" @click="closeProfessionalModal">OK</button>
      </template>
    </dx-modal>
    <dx-modal
      id="professional-modal"
      title="Profissionais solicitantes"
      v-model="requestModal.show"
      @input="closeRequestModal">
      <div class="empty" v-if="requestModal.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <div class="empty-title h6">Profissionais solicitantes</div>
        <div class="empty-subtitle">Nenhum profissional disponível</div>
      </div>
      <template v-else>
        <div class="columns">
          <div class="column col-6">
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model.trim="requestModal.search"
                placeholder="Pesquisar profissional"
                v-focus
              >
              <button class="btn btn-neutral btn-icon btn-action input-group-btn"
                      v-if="!requestModal.search">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
              <button class="btn btn-gray btn-icon btn-action input-group-btn"
                      @click="requestModal.search = ''"
                      v-else>
                <fa-icon :icon="['fal', 'times']"/>
              </button>
            </div>
          </div>
          <div class="column col-6">
            <select class="form-select"
                    v-model="requestModal.specialtyCode">
              <option value="">[Todas as especialidades]</option>
              <option v-for="(item, i) in requestModal.specialties"
                      :value="item.code" :key="i">{{ item.name }}</option>
            </select>
          </div>
        </div>
        <table class="table">
          <tr>
            <th style="width: 50px;">
              <label class="form-checkbox">
                <input type="checkbox"
                       @change="selectAllRequest"
                       :checked="getRequestSelected"
                       :indeterminate.prop="isNumber(getRequestSelected)">
                <i class="form-icon"></i>
              </label>
            </th>
            <th>Profissional</th>
          </tr>
          <tr v-for="(item, i) in filteredRequest" :key="i">
            <td>
              <label class="form-checkbox">
                <input type="checkbox" v-model="item.selected">
                <i class="form-icon"></i>
              </label>
            </td>
            <td>
              {{ item.name }}
              <small class="text-secondary">
                ({{ item.council.name }}-{{ item.council.state }} {{ item.council.record }})
              </small>
            </td>
          </tr>
        </table>
      </template>
      <template slot="footer">
        <button class="btn" @click="closeRequestModal">OK</button>
      </template>
    </dx-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import fuzzy from 'fuzzy';
import formMixin from '@/mixins/form';
import { modalities } from '@/data/expense-modalities';
import * as professionalApi from '@/app/professional/api';
import { isNumber } from 'lodash';

export default {
  mixins: [formMixin],
  data() {
    return {
      loading: false,
      printing: false,
      modalities,
      places: [],
      insuranceModal: {
        items: [],
        search: '',
        show: false,
      },
      professionalModal: {
        items: [],
        specialties: [],
        search: '',
        specialtyCode: '',
        show: false,
      },
      requestModal: {
        items: [],
        specialties: [],
        search: '',
        specialtyCode: '',
        show: false,
      },
      expenseModal: {
        items: [],
        search: '',
        modality: '',
        show: false,
      },
      filters: {
        type: 'detailed',
        group: 'insurance',
        sort: 'date',
        paid: '',
        return: false,
        courtesy: false,
        startDate: '',
        endDate: '',
        placeId: '',
        status: '',
      },
    };
  },
  validations() {
    const rules = {
      filters: {
        startDate: { date, required },
        endDate: { date, required },
      },
    };
    if (this.filters.startDate) {
      rules.filters.endDate = {
        date,
        required,
        minDate: minDate(this.filters.startDate),
      };
    }

    return rules;
  },
  async mounted() {
    this.loading = true;
    await this.loadPlaces();
    await this.loadSpecialties();
    await this.loadInsurances();
    await this.loadProfessionals();
    await this.loadProfessionalRequesters();
    await this.loadExpenses();
    this.filters.startDate = moment()
      .startOf('month')
      .format('YYYY-MM-DD');
    this.filters.endDate = moment()
      .endOf('month')
      .format('YYYY-MM-DD');
    this.loading = false;
  },
  computed: {
    filteredInsurances() {
      let { items } = this.insuranceModal;

      if (this.insuranceModal.search.trim()) {
        const results = fuzzy.filter(this.insuranceModal.search, items, {
          extract: (el) => {
            if (el.name) {
              return this.removeAccentuation(el.name);
            }
            return '';
          },
        });
        items = results.map(({ original }) => original);
      }

      return items;
    },
    filteredProfessionals() {
      let { items } = this.professionalModal;

      if (this.professionalModal.specialtyCode) {
        const professionals = this.professionalModal.items
          .filter(({ specialties }) => specialties
            .some(({ code }) => code === this.professionalModal.specialtyCode));

        items = items.filter(({ id }) => professionals.some(item => item.id === id));
      }

      if (this.professionalModal.search.trim()) {
        const results = fuzzy.filter(this.professionalModal.search, items, {
          extract: (el) => {
            if (el.name) {
              return this.removeAccentuation(el.name);
            }
            return '';
          },
        });
        items = results.map(({ original }) => original);
      }

      return items;
    },
    filteredRequest() {
      let { items } = this.requestModal;

      if (this.requestModal.specialtyCode) {
        const professionals = this.requestModal.items
          .filter(({ specialty }) => specialty.code === this.requestModal.specialtyCode);

        items = items.filter(({ id }) => professionals.some(item => item.id === id));
      }

      if (this.requestModal.search.trim()) {
        const results = fuzzy.filter(this.requestModal.search, items, {
          extract: (el) => {
            if (el.name) {
              return this.removeAccentuation(el.name);
            }
            return '';
          },
        });
        items = results.map(({ original }) => original);
      }

      return items;
    },
    filteredExpenses() {
      let { items } = this.expenseModal;

      if (this.expenseModal.modality) {
        items = items
          .filter(({ modality }) => modality === this.expenseModal.modality);
      }

      if (this.expenseModal.search.trim()) {
        const results = fuzzy.filter(this.expenseModal.search, items, {
          extract: (el) => {
            if (el.name) {
              return this.removeAccentuation(el.name);
            }
            return '';
          },
        });
        items = results.map(({ original }) => original);
      }

      return items;
    },
    getInsurancesSelected() {
      if (!this.filteredInsurances || this.filteredInsurances.length === 0) {
        return false;
      }

      const insurancesSelected = this.filteredInsurances.filter(item => item.selected);
      if (insurancesSelected.length === 0) {
        return false;
      }
      if (insurancesSelected.length === this.filteredInsurances.length) {
        return true;
      }
      return insurancesSelected.length;
    },
    getProfessionalsSelected() {
      if (!this.filteredProfessionals || this.filteredProfessionals.length === 0) {
        return false;
      }

      const professionalsSelected = this.filteredProfessionals.filter(item => item.selected);
      if (professionalsSelected.length === 0) {
        return false;
      }
      if (professionalsSelected.length === this.filteredProfessionals.length) {
        return true;
      }
      return professionalsSelected.length;
    },
    getRequestSelected() {
      if (!this.filteredRequest || this.filteredRequest.length === 0) {
        return false;
      }

      const professionalsSelected = this.filteredRequest.filter(item => item.selected);
      if (professionalsSelected.length === 0) {
        return false;
      }
      if (professionalsSelected.length === this.filteredRequest.length) {
        return true;
      }
      return professionalsSelected.length;
    },
    getExpensesSelected() {
      if (!this.filteredExpenses || this.filteredExpenses.length === 0) {
        return false;
      }

      const expensesSelected = this.filteredExpenses.filter(item => item.selected);
      if (expensesSelected.length === 0) {
        return false;
      }
      if (expensesSelected.length === this.filteredExpenses.length) {
        return true;
      }
      return expensesSelected.length;
    },
  },
  methods: {
    isNumber,
    loadPlaces() {
      const params = {
        limit: 0,
        type: 'clinical',
      };
      return this.$httpX.get('/places', { params })
        .then(({ data }) => {
          this.places = data.items;
        })
        .catch(this.$toast.error);
    },
    loadInsurances() {
      const params = {
        limit: 0,
        active: true,
      };
      return this.$http.get('/insurance-plans', { params })
        .then(({ data }) => {
          this.insuranceModal.items = data.items.map(item => ({
            id: item.plan.id,
            name: item.customName,
            selected: true,
          }));
        })
        .catch(this.$toast.error);
    },
    loadProfessionals() {
      return professionalApi.allActive()
        .then((data) => {
          data.items.forEach((item) => {
            this.professionalModal.items.push({
              id: item.id,
              name: item.name,
              specialties: item.professional.specialties,
              selected: true,
            });
          });
        })
        .catch(this.$toast.error);
    },
    loadProfessionalRequesters() {
      const params = {
        limit: 0,
      };

      return this.$httpX.get('/professional-requesters', { params })
        .then(({ data }) => {
          data.items.forEach((item, i) => {
            this.requestModal.items.push({
              id: i,
              name: item.name,
              council: item.council,
              specialty: item.specialty,
              selected: true,
            });
          });
        })
        .catch(this.$toast.error);
    },
    loadExpenses() {
      const params = {
        limit: 0,
        active: true,
      };
      return this.$httpX.get('/expenses', { params })
        .then(({ data }) => {
          this.expenseModal.items = data.items.map(item => ({
            id: item.id,
            name: item.name,
            modality: item.modality,
            selected: true,
          }));
        })
        .catch(this.$toast.error);
    },
    loadSpecialties() {
      const params = {
        limit: 0,
        isSpecialty: true,
      };

      return this.$http.get('/services/cbos', { params })
        .then(({ data }) => {
          this.professionalModal.specialties = data.items;
          this.requestModal.specialties = data.items;
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar as especialidades');
        });
    },
    async openInsuranceModal() {
      this.insuranceModal.show = true;
    },
    async openProfessionalModal() {
      this.professionalModal.show = true;
    },
    async openRequestModal() {
      this.requestModal.show = true;
    },
    async openExpenseModal() {
      this.expenseModal.show = true;
    },
    closeInsuranceModal() {
      this.insuranceModal.search = '';
      this.insuranceModal.show = false;
    },
    closeProfessionalModal() {
      this.professionalModal.search = '';
      this.professionalModal.specialtyCode = '';
      this.professionalModal.show = false;
    },
    closeRequestModal() {
      this.requestModal.search = '';
      this.requestModal.specialtyCode = '';
      this.requestModal.show = false;
    },
    closeExpenseModal() {
      this.expenseModal.search = '';
      this.expenseModal.modality = '';
      this.expenseModal.show = false;
    },
    selectAllInsurances(e) {
      this.filteredInsurances.forEach((item) => {
        item.selected = e.target.checked;
      });
    },
    selectAllProfessionals(e) {
      this.filteredProfessionals.forEach((item) => {
        item.selected = e.target.checked;
      });
    },
    selectAllRequest(e) {
      this.filteredRequest.forEach((item) => {
        item.selected = e.target.checked;
      });
    },
    selectAllExpenses(e) {
      this.filteredExpenses.forEach((item) => {
        item.selected = e.target.checked;
      });
    },
    print(printType) {
      this.$v.filters.$touch();
      if (this.$v.filters.$error) {
        return false;
      }

      if (!this.getInsurancesSelected) {
        this.$toast.error('Selecione um ou mais convênios!');
        return false;
      }

      if (!this.getProfessionalsSelected) {
        this.$toast.error('Selecione um ou mais profissionais!');
        return false;
      }

      if (!this.getExpensesSelected) {
        this.$toast.error('Selecione um ou mais procedimentos!');
        return false;
      }

      this.printing = true;

      const params = {
        printType,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        type: this.filters.type,
        group: this.filters.group,
        return: this.filters.return,
        sort: this.filters.sort,
      };

      if (this.getInsurancesSelected !== true) {
        params.insuranceIds = this.insuranceModal.items
          .filter(({ selected }) => selected)
          .map(item => item.id).join(',');
      }

      if (this.getProfessionalsSelected !== true) {
        params.professionalIds = this.professionalModal.items
          .filter(({ selected }) => selected)
          .map(item => item.id).join(',');
      }

      if (this.getRequestSelected !== true) {
        params.requestCouncils = this.requestModal.items
          .filter(({ selected }) => selected)
          .map(item => `${item.council.name}_${item.council.state}_${item.council.record}`)
          .join(',');
      }

      if (this.getExpensesSelected !== true) {
        params.expenseIds = this.expenseModal.items
          .filter(({ selected }) => selected)
          .map(item => item.id).join(',');
      }

      if (this.filters.paid !== '') {
        params.paid = this.filters.paid;
      }

      if (this.filters.courtesy !== '') {
        params.courtesy = this.filters.courtesy;
      }

      if (this.filters.status) {
        params.status = this.filters.status;
      }

      if (this.filters.placeId) {
        params.placeId = this.filters.placeId;
      }

      if (printType === 'sheet') {
        return this.$file
          .download('/reports/billing-demonstrative', params, {
            method: 'POST',
            name: 'Demonstrativo de faturamento.xlsx',
          })
          .catch(() => {})
          .finally(() => {
            this.printing = false;
          });
      }

      return this.$file
        .print('/reports/billing-demonstrative', params, { method: 'POST' })
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
  },
};
</script>

<style lang="scss">
  @import '../../../assets/scss/variables';
  @import '../../../assets/scss/mixins';

  .billing-statement-page {
    .button-types {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: $layout-spacing;
      grid-row-gap: $layout-spacing;
      margin: $layout-spacing-lg 0;
      .type {
        background-color: $gray-color-ultra-light;
        border: $border-width solid $border-color;
        border-radius: $border-radius;
        cursor: pointer;
        padding: $layout-spacing 0;
        text-align: center;
        &:hover {
          border: $border-width solid $gray-color;
        }
      }
    }
  }
</style>
